// Sprint 17 A3: Deal detail with intelligence sidebar + D4 design upgrade
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { deleteDeal, updateDeal, useDeal, usePipelines, type Deal } from "@/hooks/use-deals";
import { useContactTimeline } from "@/hooks/use-contacts";
import { useTaskList } from "@/hooks/use-tasks";
import { useDealIntelligence } from "@/hooks/use-crm-deal-intelligence";
import { useContactScoreBreakdown } from "@/hooks/use-crm-contact-score";
import { useOrganizationMembers } from "@/hooks/use-organization-members";
import type { OrganizationMemberRecord } from "@/lib/organization-client";
import { formatCurrency } from "@/lib/format";
import { formatDate } from "@/lib/i18n";
import {
  buildCrmProfileSections as buildCrmDealProfileSections,
} from "@ease/crm/profile-sections";
import { ContactTimeline } from "../contacts/contact-timeline";
import { DealIntelligencePanel } from "@/components/crm/deal-intelligence-panel";
import { DealMomentumBadge } from "@/components/crm/deal-momentum-badge";
import { CrmProfileSectionsCard } from "@/components/crm/profile-sections-card";
import { RelatedTaskSnapshotCard } from "@/components/crm/related-task-snapshot-card";
import { ScoreBreakdown } from "@/components/crm/score-breakdown";
import { SlideOverPanel } from "@/components/crm/slide-over-panel";
import { Skeleton } from "@/components/skeleton";
import { Calendar, User, Clock, TrendingUp } from "lucide-react";

interface DealDetailProps {
  dealId: string;
  onClose: () => void;
}

const OPEN_DEAL_TASK_STATUSES = new Set(["pending", "in_progress"]);

/** Slide-over deal detail panel with AI intelligence + stage progress bar. */
export function DealDetail({ dealId, onClose }: DealDetailProps) {
  const { deal, isLoading, error, refetch } = useDeal(dealId);
  const { pipelines } = usePipelines();
  const {
    data: organizationMembers,
    isLoading: areOrganizationMembersLoading,
    error: organizationMembersError,
  } = useOrganizationMembers();
  const { entries: timeline, isLoading: timelineLoading } = useContactTimeline(
    deal?.personId ?? null,
  );
  const {
    tasks: dealTasks,
    isLoading: isDealTasksLoading,
    error: dealTasksError,
  } = useTaskList({ dealId, limit: 10 });
  const { data: intelligence } = useDealIntelligence(dealId);
  const { data: scoreBreakdown } = useContactScoreBreakdown(deal?.personId ?? null);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Find the pipeline and current stage for the progress bar
  const pipeline = pipelines.find((p) => p.id === deal?.pipelineId);
  const stages = pipeline?.stages ?? [];
  const currentStageIdx = stages.findIndex((s) => s.id === deal?.stageId);
  const memberOptions = useMemo(() => organizationMembers ?? [], [organizationMembers]);
  const memberLabelById = useMemo(
    () => new Map(memberOptions.map((member) => [member.id, formatOrganizationMemberLabel(member)])),
    [memberOptions],
  );
  const openDealTasks = useMemo(
    () => dealTasks.filter((task) => OPEN_DEAL_TASK_STATUSES.has(task.status)),
    [dealTasks],
  );
  const visibleDealTasks = openDealTasks.slice(0, 4);

  if (isLoading) {
    return (
      <SlideOverPanel onClose={onClose}>
        <div className="space-y-4 p-6">
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="mt-4 h-3 w-full rounded-full" />
        </div>
      </SlideOverPanel>
    );
  }

  if (error || !deal) {
    return (
      <SlideOverPanel onClose={onClose}>
        <div className="p-6 text-center text-sm text-[var(--color-destructive)]">{error ?? "Deal not found"}</div>
      </SlideOverPanel>
    );
  }

  const dealProfileSections = buildCrmDealProfileSections(deal.customFields, {
    sectionKeys: ["dealProfileSections", "crmProfileSections", "profileSections"],
  }).sections;

  const handleDelete = async () => {
    const confirmed = typeof window === "undefined" || window.confirm("Delete this deal?");
    if (!confirmed || isDeleting) return;

    setIsDeleting(true);
    setActionError(null);
    try {
      await deleteDeal(deal.id);
      onClose();
    } catch {
      setActionError("Failed to delete deal");
      setIsDeleting(false);
    }
  };

  return (
    <SlideOverPanel onClose={onClose}>
      {/* Header */}
      <div className="border-b border-[var(--color-border)] p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="truncate text-lg font-semibold text-[var(--color-foreground)]">{deal.title}</h2>
              {intelligence && (
                <DealMomentumBadge
                  direction={intelligence.momentum.direction}
                  score={intelligence.momentum.score}
                  compact
                />
              )}
            </div>
            {deal.value != null && (
              <p
                className="mt-1 text-xl font-bold text-[var(--color-primary)]"
                style={{ fontVariantNumeric: "tabular-nums" }}
              >
                {formatCurrency(deal.value, deal.currency)}
              </p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setActionError(null);
                setIsEditing(true);
              }}
              className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-xs font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={isDeleting}
              className="rounded-md border border-[var(--color-destructive)]/30 px-3 py-1.5 text-xs font-medium text-[var(--color-destructive)] hover:bg-[var(--color-destructive)]/10 disabled:opacity-50"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
        {actionError && (
          <p className="mt-3 rounded-md border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-3 py-2 text-sm text-[var(--color-destructive)]">
            {actionError}
          </p>
        )}

        {/* Stage progress bar */}
        {stages.length > 0 && (
          <div className="mt-4">
            <div className="flex items-center gap-1">
              {stages.map((stage, idx) => {
                const isActive = idx === currentStageIdx;
                const isPast = idx < currentStageIdx;
                const isWin = stage.isWinStage && isActive;
                const isLoss = stage.isLossStage && isActive;

                return (
                  <div key={stage.id} className="stagger-item flex-1 flex flex-col items-center gap-1">
                    <div
                      className="h-2 w-full rounded-full transition-all duration-300"
                      style={{
                        backgroundColor: isWin
                          ? "var(--color-success)"
                          : isLoss
                            ? "var(--color-destructive)"
                            : isActive || isPast
                              ? stage.color ?? "var(--color-primary)"
                              : "var(--color-muted)",
                        opacity: isPast ? 0.5 : 1,
                      }}
                    />
                    <span
                      className={`text-[10px] leading-tight ${
                        isActive ? "font-semibold text-[var(--color-foreground)]" : "text-[var(--color-muted-foreground)]"
                      }`}
                    >
                      {stage.name}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {isEditing && (
          <DealEditForm
            deal={deal}
            members={memberOptions}
            areMembersLoading={areOrganizationMembersLoading}
            membersError={organizationMembersError}
            onCancel={() => setIsEditing(false)}
            onSaved={async () => {
              await refetch();
              setIsEditing(false);
            }}
          />
        )}

        {/* AI Intelligence Panel */}
        <DealIntelligencePanel dealId={dealId} />

        <RelatedTaskSnapshotCard
          title="Open Deal Tasks"
          description="Current follow-ups, SLA signals, evidence, and safe operator actions tied to this opportunity."
          tasks={visibleDealTasks}
          openTaskCount={openDealTasks.length}
          isLoading={isDealTasksLoading}
          error={dealTasksError}
          viewAllHref={`/crm/tasks?dealId=${encodeURIComponent(deal.id)}`}
          emptyMessage="No open tasks for this deal."
          overflowMessage={(visibleCount, totalCount) => `Showing ${visibleCount} of ${totalCount} open tasks for this deal.`}
          evidenceLabel="Deal task reusable evidence"
        />

        <CrmProfileSectionsCard
          sections={dealProfileSections}
          title="Opportunity profile"
          description="Configured opportunity facts from tenant sales, service, commerce, and support systems."
          className="card-hover rounded-lg bg-transparent"
        />

        {/* Contact Score Breakdown */}
        {scoreBreakdown && (
          <div className="card-hover rounded-lg border border-[var(--color-border)] p-4">
            <h3 className="mb-3 text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
              Contact Score
            </h3>
            <ScoreBreakdown
              totalScore={scoreBreakdown.totalScore}
              tier={scoreBreakdown.tier}
              categories={scoreBreakdown.categories}
              improvementSuggestion={scoreBreakdown.improvementSuggestion}
            />
          </div>
        )}

        {/* Deal details — card style */}
        <div className="card-hover rounded-lg border border-[var(--color-border)] p-4">
          <h3 className="mb-3 text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">Details</h3>
          <div className="space-y-2.5">
            <InfoRow icon={<TrendingUp className="h-3.5 w-3.5" />} label="Score" value={String(deal.score)} />
            {deal.expectedCloseDate && (
              <InfoRow icon={<Calendar className="h-3.5 w-3.5" />} label="Expected Close" value={formatDate(deal.expectedCloseDate)} />
            )}
            {deal.closedAt && (
              <InfoRow icon={<Calendar className="h-3.5 w-3.5" />} label="Closed" value={formatDate(deal.closedAt)} />
            )}
            {deal.lostReason && <InfoRow label="Lost Reason" value={deal.lostReason} />}
            {deal.winReason && <InfoRow label="Win Reason" value={deal.winReason} />}
            {deal.ownerId && <InfoRow icon={<User className="h-3.5 w-3.5" />} label="Owner" value={formatDealOwner(deal.ownerId, memberLabelById)} />}
            <InfoRow icon={<Clock className="h-3.5 w-3.5" />} label="Stage Entered" value={formatDate(deal.stageEnteredAt)} />
            <InfoRow icon={<Clock className="h-3.5 w-3.5" />} label="Created" value={formatDate(deal.createdAt)} />
          </div>
        </div>

        {/* Associated contact timeline */}
        {deal.personId && (
          <div>
            <h3 className="mb-2 text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">Contact Activity</h3>
            <ContactTimeline entries={timeline} isLoading={timelineLoading} />
          </div>
        )}
      </div>
    </SlideOverPanel>
  );
}

function DealEditForm({
  deal,
  members,
  areMembersLoading,
  membersError,
  onCancel,
  onSaved,
}: {
  deal: Deal;
  members: OrganizationMemberRecord[];
  areMembersLoading: boolean;
  membersError: string | null;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const [title, setTitle] = useState(deal.title);
  const [value, setValue] = useState(deal.value != null ? String(deal.value / 100) : "");
  const [expectedCloseDate, setExpectedCloseDate] = useState(toDateInputValue(deal.expectedCloseDate));
  const [ownerId, setOwnerId] = useState(deal.ownerId ?? "");
  const [notes, setNotes] = useState(stringField(deal.customFields.notes));
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ownerIsKnown = ownerId ? members.some((member) => member.id === ownerId) : true;

  useEffect(() => {
    setTitle(deal.title);
    setValue(deal.value != null ? String(deal.value / 100) : "");
    setExpectedCloseDate(toDateInputValue(deal.expectedCloseDate));
    setOwnerId(deal.ownerId ?? "");
    setNotes(stringField(deal.customFields.notes));
  }, [deal]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const trimmedTitle = title.trim();
    if (!trimmedTitle || isSaving) return;

    const parsedValue = Number.parseFloat(value);
    const valueCents = value.trim() && Number.isFinite(parsedValue) ? Math.round(parsedValue * 100) : null;
    const customFields = { ...deal.customFields };
    const trimmedNotes = notes.trim();
    if (trimmedNotes) {
      customFields.notes = trimmedNotes;
    } else {
      delete customFields.notes;
    }

    setIsSaving(true);
    setError(null);
    try {
      await updateDeal(deal.id, {
        title: trimmedTitle,
        value: valueCents,
        expectedCloseDate: expectedCloseDate || null,
        ownerId: ownerId || null,
        customFields,
      });
      await onSaved();
    } catch {
      setError("Failed to update deal");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="grid gap-1 text-xs font-medium text-[var(--color-muted-foreground)]">
          Deal name
          <input className="rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]" value={title} onChange={(event) => setTitle(event.target.value)} />
        </label>
        <label className="grid gap-1 text-xs font-medium text-[var(--color-muted-foreground)]">
          Value
          <input className="rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]" inputMode="decimal" value={value} onChange={(event) => setValue(event.target.value)} />
        </label>
        <label className="grid gap-1 text-xs font-medium text-[var(--color-muted-foreground)]">
          Expected close
          <input className="rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]" type="date" value={expectedCloseDate} onChange={(event) => setExpectedCloseDate(event.target.value)} />
        </label>
        <label className="grid gap-1 text-xs font-medium text-[var(--color-muted-foreground)]">
          Owner
          <select
            value={ownerId}
            onChange={(event) => setOwnerId(event.target.value)}
            disabled={areMembersLoading || Boolean(membersError)}
            aria-label="Assign deal owner"
            className="rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
          >
            <option value="">
              {areMembersLoading
                ? "Loading owners..."
                : membersError
                  ? "Owner list unavailable"
                  : "Unassigned"}
            </option>
            {!ownerIsKnown && (
              <option value={ownerId}>
                {ownerId}
              </option>
            )}
            {members.map((member) => (
              <option key={member.id} value={member.id}>
                {formatOrganizationMemberLabel(member)}
              </option>
            ))}
          </select>
          {membersError && (
            <span className="text-xs text-[var(--color-destructive)]">
              Owner selection is temporarily unavailable. Existing owner assignment will be preserved.
            </span>
          )}
        </label>
        <label className="grid gap-1 text-xs font-medium text-[var(--color-muted-foreground)] sm:col-span-2">
          Notes
          <textarea className="min-h-20 rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]" value={notes} onChange={(event) => setNotes(event.target.value)} />
        </label>
      </div>
      {error && <p className="mt-3 text-sm text-[var(--color-destructive)]">{error}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-sm text-[var(--color-foreground)]">
          Cancel
        </button>
        <button type="submit" disabled={isSaving || !title.trim()} className="rounded-md bg-[var(--color-primary)] px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50">
          {isSaving ? "Saving..." : "Save changes"}
        </button>
      </div>
    </form>
  );
}

function toDateInputValue(value: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
}

function formatOrganizationMemberLabel(member: { name: string | null; email: string }): string {
  return member.name?.trim() || member.email;
}

function formatDealOwner(ownerId: string, memberLabelById: Map<string, string>): string {
  return memberLabelById.get(ownerId) ?? ownerId;
}

function stringField(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function InfoRow({ icon, label, value }: { icon?: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {icon && <span className="text-[var(--color-muted-foreground)]">{icon}</span>}
      <span className="text-[var(--color-muted-foreground)] min-w-[100px]">{label}</span>
      <span className="text-[var(--color-foreground)] font-medium">{value}</span>
    </div>
  );
}
